import { useState } from "react";
import { AxiosResponse } from "axios";
import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { cooKeys, customEvent, getCookie, setCookie } from "goi_common";

import { QuotationResponse } from "@/apis/sangjoQuotation";
import { isLoggedInState } from "@/atoms/accountState";
import { matchRequestResDataState } from "@/atoms/match/matchRequestResDataState";
import { quotationRequestState } from "@/atoms/quotationRequestState";
import { temporaryMatchRequestState } from "@/containers/MatchContainer/atoms";
import { fireGuestSubmitEvents } from "@/containers/MatchContainer/fireGuestSubmitEvents";
import useSangjoQuotationRequests from "@/hooks/useSangjoQuotationRequests";
import { ParlorDetailProps } from "@/types/parlor";
import { Axios } from "@/utils/axios";
import { allowIntentionalLeave } from "./useParlorRouterChangeLoad";

/**
 * E5 견적 제출. 기존 질문 폼(ParlorRequestQuestionStepByStep handleSubmit)과 **같은 서버 호출 · 같은 완료 이벤트**를 쓴다.
 * 그래야 A(기존) · E5 의 견적 제출 · 로그인 · 리드 지표가 같은 기준으로 비교된다.
 *
 * 다른 점: 빈소 평형을 묻지 않아 memo 에 평형 대신 「3일장 기준」 을 남기고, 로그인 화면 대신 onReady(받는 방법)로 넘긴다.
 * 기존 폼과 겹치는 로직은 실험이 끝나면 남는 쪽 하나로 합친다(wiki/backlog.md).
 */
export const useE5QuotationSubmit = (parlor: ParlorDetailProps) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const isLoggedIn = useRecoilValue(isLoggedInState);
  const setResponseData = useSetRecoilState(matchRequestResDataState);
  const [, setTemporaryMatchRequest] = useRecoilState(temporaryMatchRequestState);
  const setQuotationRequest = useSetRecoilState(quotationRequestState);
  const { setQuotationRequestCookie, mutateSangjoQuotation } = useSangjoQuotationRequests();

  const submit = ({
    status,
    condolers,
    onReady,
  }: {
    /** 장례준비긴급도_LIST 값 그대로 */
    status: string;
    /** 조문객예상인원_LIST 값 그대로 */
    condolers: string;
    /** 저장이 끝나면 부른다. 받는 방법(카카오 · 번호)으로 보낸다 */
    onReady: () => void;
  }) => {
    if (loading) return;
    setLoading(true);

    customEvent({
      newGtm: { path: "parlor_detail_request", trigger_and_target: "submit_conversion" },
    });

    const body = {
      status,
      funeral_sido: Number(parlor.sido),
      funeral_sigungu: Number(parlor.sigungu),
      needs_binso: condolers !== "0명(무빈소)",
      condolers: condolers === "0명(무빈소)" ? "0명" : condolers,
      is_sequential: false,
    };

    const onSuccess = ({ data }: AxiosResponse<QuotationResponse>) => {
      customEvent({
        newGtm: {
          path: "parlor_match_request",
          trigger_and_target: "funnel_done",
          properties: { outcome: "quotation" },
        },
      });
      customEvent({ newGtm: { action: "match_request_complete" }, triggerHackle: true });
      customEvent({ newGtm: { action: "match_request_complete_parlor" }, triggerHackle: true });
      fireGuestSubmitEvents(data?.status);
      allowIntentionalLeave();

      if (isLoggedIn) {
        router.push("/match/recommendation/");
        return;
      }

      setTemporaryMatchRequest(data.id);
      setResponseData(data);
      setQuotationRequest(data);
      setQuotationRequestCookie(data);

      (async () => {
        customEvent({
          newGtm: { path: "match_request", trigger_and_target: "mdl_check_result" },
          triggerHackle: true,
        });
        customEvent({
          newGtm: { path: "parlor_match_request", trigger_and_target: "mdl_check_result" },
          triggerHackle: true,
        });

        // memo 는 덧붙이는 정보라 실패해도 가입으로는 넘긴다. 여기서 멈추면 견적은 저장됐는데
        // 버튼이 「잠시만요」 에 묶여 리드가 사라진다(useCostLeadSubmit 과 같은 처리)
        try {
          const anyMemo = getCookie("match_request_memo");
          const previous = getCookie(cooKeys.quotation_request);
          const note = `${parlor.companyname} 개별 견적 페이지(E5)에서 제출한 견적 / 3일장 기준`;
          const { data: patched } = await Axios.patch(
            `/sangjo_quotation_requests/${previous.id}/`,
            {
              // memo 는 STRING(255). 넘기면 BE 가 400 을 내 PATCH 가 실패한다
              memo: (note + " | " + (anyMemo || "")).slice(0, 255),
            },
          );
          setCookie(cooKeys.quotation_request, JSON.stringify({ ...patched, id: previous.id }));
          setQuotationRequest(patched);
        } catch {
          // 저장된 견적 쿠키(setQuotationRequestCookie)는 이미 있어 로그인 뒤 흐름은 그대로다
        }
        // 로그인 화면 되짚기용. 평형은 묻지 않아 비운다
        setCookie(
          cooKeys.parlor_request_info,
          JSON.stringify({ parlorName: parlor.companyname, 평형: "" }),
        );
        setLoading(false);
        onReady();
      })();
    };

    mutateSangjoQuotation({ body, onSuccess, onError: () => setLoading(false) });
  };

  return { submit, loading };
};
